import { type Observable as ObservableType, Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/mergeMap';
import { graphql, fetchQuery } from 'react-relay';
import { query$ } from '@atlassian/jira-software-swag/src/services/query/index.tsx';
import type { IssueId } from '@atlassian/jira-software-board-common/src/index.tsx';
import { createAri } from '@atlassian/jira-platform-ari/src/index.tsx';
import getRelayEnvironment from '@atlassian/jira-relay-environment/src/index.tsx';
import type { filteredCardsQuery } from '@atlassian/jira-relay/src/__generated__/filteredCardsQuery.graphql';
import type { FilteredCardIdsLoadGql } from '../../model/work/work-types.tsx';

const filteredCardsQuery = 'FilteredCardIds';

export const getQuery = () => `
    query ${filteredCardsQuery} ($boardId: ID!, $issueIds: [ID]!, $customFilterIds: [ID]!) {
        boardScope(boardId: $boardId) {
            filteredCardIds(
                issueIds: $issueIds,
                customFilterIds: $customFilterIds
            )
        }
    }
`;

interface Params {
	boardId: string;
	issueIds: IssueId[];
	customFilterIds: string[];
}

interface ParamsWithJql {
	boardId: string;
	filterJql?: string;
	cloudId: string;
	issueIds: (string | null | undefined)[];
	customFilterIds?: (string | null | undefined)[];
}

export function fetchTMPFilteredCardIds({
	boardId,
	issueIds,
	customFilterIds,
}: Params): ObservableType<string[]> {
	return query$<FilteredCardIdsLoadGql>('', filteredCardsQuery, getQuery(), {
		boardId,
		issueIds,
		customFilterIds,
	}).flatMap(({ boardScope }) => Observable.of(boardScope.filteredCardIds));
}

export const fetchTMPFilteredCardIdsWithJql = async ({
	cloudId,
	boardId: boardIdNumber,
	issueIds,
	customFilterIds,
	filterJql,
}: ParamsWithJql) => {
	const boardIdAri = createAri({
		resourceOwner: 'jira-software',
		cloudId,
		resourceType: 'board',
		resourceId: String(boardIdNumber),
	});
	return fetchQuery<filteredCardsQuery>(
		getRelayEnvironment(),
		graphql`
			query filteredCardsQuery(
				$boardId: ID!
				$issueIds: [ID]!
				$customFilterIds: [ID]
				$filterJql: String
			) {
				boardScope(boardId: $boardId) {
					# eslint-disable-next-line @atlassian/relay/unused-fields
					filteredCardIds(
						issueIds: $issueIds
						customFilterIds: $customFilterIds
						filterJql: $filterJql
					)
				}
			}
		`,
		{ boardId: boardIdAri, issueIds, filterJql, customFilterIds },
	)
		.toPromise()
		.then((data) => data?.boardScope?.filteredCardIds?.filter(Boolean) || []);
};
